import { UNKNOWN_CANDIDATE_PROFILE_NAME } from 'modules/candidate-profile/utils/getCandidateProfileDisplayName';
import React from 'react';
import { Box, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  UUID_REGEX,
  SPECIFIC_SUGGESTION_FIELD_REGEX,
  SPECIFIC_SUGGESTION_REGEX,
  BLOCKQUOTE_REGEX
} from 'core/constants/regex';
import { CandidateProfileChip } from 'modules/candidate-profile/components/CandidateProfileChip';
import { candidateProfileSearchConversationSx } from 'modules/candidate-profile/styles';
import { getCandidateProfileLink } from 'modules/candidate-profile/utils/getCandidateProfileLink';
import {
  CandidateProfileListProps,
  CandidateProfileListType
} from 'modules/candidate-profile/types/CandidateProfileListProps';

export type CandidateProfileLinkData = {
  id: string;
  full_name?: string | null;
};

type ParseMessageWithCandidatesAndChipsProps = {
  messageBody: string;
  candidateProfiles: CandidateProfileLinkData[];
  /**
   * `field` is present on a removal chip and names the filter to clear. The
   * label is prose in the recruiter's language and cannot be matched back to a
   * filter, so the field is what makes the click actionable.
   */
  onChipClick?: (suggestion: string, field?: string) => void;
  searchParams?: Record<string, string>;
  candidateProfileListProps?: CandidateProfileListProps;
};

/**
 * Parses a message body and replaces UUIDs with candidate names and links,
 * <specific_suggestion> tags with chips, and <blockquote> tags with a quote
 * block. Anything not matched is passed through as text, so an unhandled tag
 * shows up verbatim in the conversation.
 */
export const parseMessageWithCandidatesAndChips = ({
  messageBody,
  candidateProfiles,
  onChipClick,
  searchParams = {},
  candidateProfileListProps = {
    type: CandidateProfileListType.CANDIDATES_LIST
  }
}: ParseMessageWithCandidatesAndChipsProps): React.ReactNode[] => {
  // Create a lookup object for fast candidate access
  const candidateLookup: Record<string, CandidateProfileLinkData> = {};
  candidateProfiles.forEach((candidate) => {
    candidateLookup[candidate.id] = candidate;
  });

  // Combined regex matching UUIDs, specific_suggestion tags and blockquotes.
  // Blockquote goes last on purpose: each alternative contributes a capture
  // group, so appending keeps `match[2]` pointing at the suggestion content.
  const combinedRegex = new RegExp(
    `(${SPECIFIC_SUGGESTION_REGEX.source}|${UUID_REGEX.source}|${BLOCKQUOTE_REGEX.source})`,
    'g'
  );

  const result: React.ReactNode[] = [];
  const matches = Array.from(messageBody.matchAll(combinedRegex));
  let lastIndex = 0;
  let chipKey = 0;

  matches.forEach((match, matchIndex) => {
    // Add text before the match
    if (match.index !== undefined && match.index > lastIndex) {
      const textContent = messageBody.substring(lastIndex, match.index);
      result.push(
        <React.Fragment key={`text-before-${matchIndex}`}>
          {textContent}
        </React.Fragment>
      );
    }

    const fullMatch = match[0];
    const suggestionContent = match[2];
    const blockquoteContent = match[4];

    if (fullMatch.startsWith('<blockquote>')) {
      // Trimmed because the tags sit on their own lines, and the surrounding
      // container is pre-wrap -- untrimmed content renders a blank first line.
      result.push(
        <Box
          key={`blockquote-${matchIndex}`}
          component="blockquote"
          sx={candidateProfileSearchConversationSx.conversationBlockquote}
        >
          {(blockquoteContent ?? '').trim()}
        </Box>
      );
    } else if (fullMatch.startsWith('<specific_suggestion')) {
      // Not '<specific_suggestion>': a removal chip carries a field attribute.
      const field = fullMatch.match(SPECIFIC_SUGGESTION_FIELD_REGEX)?.[1];

      result.push(
        <CandidateProfileChip
          key={`chip-${chipKey++}`}
          label={suggestionContent}
          size="small"
          onClick={() => onChipClick?.(suggestionContent, field)}
        />
      );
    } else if (new RegExp(UUID_REGEX.source).test(fullMatch)) {
      // Strip angle brackets from UUID: <uuid> -> uuid
      const uuid = fullMatch.replace(/[<>]/g, '');
      const candidate = candidateLookup[uuid];

      if (!candidate) {
        result.push(
          <span
            key={`loading-${uuid}`}
            style={{ fontStyle: 'italic', color: '#999' }}
          >
            Loading...
          </span>
        );
      } else {
        const candidateName =
          candidate.full_name || UNKNOWN_CANDIDATE_PROFILE_NAME;
        const profilePath = getCandidateProfileLink({
          candidateProfileListProps,
          id: uuid,
          searchParams: new URLSearchParams(searchParams)
        });

        result.push(
          <Link
            key={`link-${uuid}`}
            component={RouterLink}
            to={profilePath}
            underline="always"
            sx={{ fontWeight: 600 }}
          >
            {candidateName}
          </Link>
        );
      }
    }

    if (match.index !== undefined) {
      lastIndex = match.index + fullMatch.length;
    }
  });

  // Add remaining text
  if (lastIndex < messageBody.length) {
    const textContent = messageBody.substring(lastIndex);
    result.push(<React.Fragment key="text-end">{textContent}</React.Fragment>);
  }

  // Deduplicate results - remove duplicate candidate links/loading states
  const seenUuids = new Set<string>();
  const filteredResult = result.filter((item) => {
    if (React.isValidElement(item)) {
      const key = item.key;
      // Check if this is a candidate link or loading state
      if (
        typeof key === 'string' &&
        (key.startsWith('link-') || key.startsWith('loading-'))
      ) {
        const uuid = key.replace(/^(link-|loading-)/, '');
        if (seenUuids.has(uuid)) {
          return false; // Skip duplicate
        }
        seenUuids.add(uuid);
      }
    }
    return true;
  });

  // If no matches found, return original message
  return filteredResult.length > 0 ? filteredResult : [messageBody];
};
